import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Truncate from 'react-truncate';
import cn from 'classnames';
import { Container, Row, Col } from 'reactstrap';
import 'react-table/react-table.css';
import { injectIntl } from 'react-intl';

// Components
import { Icon } from 'components/atoms/Icon';
import Price from 'components/atoms/Price';
import Button from 'components/atoms/Button';
import Checkbox from 'components/atoms/Checkbox';

// Constants
import { SERVICES_TYPE } from 'constants/services';

import './PackagesTable.scss';

// Utils
import { calcPackageTotalPrice } from 'utils/prices';

const ns = 'packages-table';
const { MENU } = SERVICES_TYPE;

class PackagesTable extends Component {
  static getDerivedStateFromProps(props) {
    if (props.packages.length > 0) {
      const allServices = [];
      props.packages.map(pack =>
        pack.services.map(service => allServices.push(service))
      );
      const allServicesIds = [];
      const joinedServices = allServices.filter(service => {
        const result = allServicesIds.indexOf(service.id) === -1;
        if (result) {
          allServicesIds.push(service.id);
        }
        return result;
      });

      return { joinedServices };
    }
    return null;
  }

  constructor(props) {
    super(props);
    this.collapseService = this.collapseService.bind(this);
    this.getButtonContent = this.getButtonContent.bind(this);
    this.state = {
      joinedServices: []
    };
  }

  collapseService(serviceId) {
    const { joinedServices } = this.state;
    this.setState({
      joinedServices: joinedServices.map(service => {
        if (service.id === serviceId) {
          service.isExpanded = !service.isExpanded;
        }
        return service;
      })
    });
  }

  getServicesCell(row) {
    const { promotion, price, discountedPrice } = row.value;
    const classProps = cn(`${ns}__service`, `${ns}__border-left`, `h-100`, {
      [`${ns}__service--active`]: row.value.isExpanded
    });
    const descriptionClassProps = cn(`${ns}__collapse`, 'd-flex', {
      [`${ns}__collapse--closed`]: !row.value.isExpanded
    });
    return (
      <Container className={classProps}>
        <Row>
          <Col sm={1} className={'text-center'}>
            {row.value.description && (
              <Icon
                type={`chevron__${row.value.isExpanded ? 'up' : 'down'}`}
                className={`${ns}__icon`}
                onClick={() => this.collapseService(row.value.id)}
              />
            )}
          </Col>
          <Col sm={promotion ? 6 : 8}>
            <Truncate lines={2} className={`${ns}__title`}>
              {row.value.name}
            </Truncate>
          </Col>
          {promotion && (
            <Col sm={2} className={'text-center'}>
              <p className={`${ns}__price--promotion`}>
                {row.value.showPrice && <Price value={price} type={MENU} />}
              </p>
            </Col>
          )}
          <Col sm={3} className={'text-center'}>
            {row.value.showPrice && (
              <Price value={promotion ? discountedPrice : price} type={MENU} />
            )}
          </Col>
        </Row>
        {row.value.description && (
          <Row className={descriptionClassProps}>
            <Col className={`${ns}__description`}>{row.value.description}</Col>
          </Row>
        )}
      </Container>
    );
  }

  getSelectionCell(row) {
    const { selectService, intl } = this.props;
    const rowService = row.row.service;
    const classProps = cn(`${ns}__border-right`, `h-100`, {
      [`${ns}__service--active`]: rowService.isExpanded
    });
    /* eslint-disable react/no-array-index-key */
    return (
      <Container className={classProps}>
        <Row className={'flex-nowrap h-100'}>
          {row.value.map((col, index) => (
            <Col
              key={`${rowService.id}-${index}`}
              className={`${ns}__service ${ns}__sticky-col ${ns}__border-right text-center`}
            >
              {typeof col === 'boolean' && (
                <Checkbox
                  isSelected={col}
                  onClick={() => selectService(rowService.id, index)}
                />
              )}
              {col === 'mandatory' && (
                <Icon
                  key={rowService.id}
                  type={'check'}
                  aria-label={intl.formatMessage({
                    id: 'c8.aria.check.mark',
                    defaultMessage: 'Check mark'
                  })}
                />
              )}
            </Col>
          ))}
        </Row>
      </Container>
    );
  }

  getButtonContent(isSelected, packagePrice, isAriaLabel) {
    const { canUpdate, intl, activePackage } = this.props;
    if (isSelected && !canUpdate) {
      return intl.formatMessage({
        id: 'c8.services.page.packages.modal.button.remove'
      });
    } else if (isSelected && canUpdate) {
      return intl.formatMessage({
        id: 'c8.services.page.packages.modal.button.update'
      });
    } else if (isAriaLabel) {
      return intl.formatMessage(
        {
          id: 'c8.services.page.packages.modal.button.add.active.package',
          defaultMessage: 'ADD {activePackage}'
        },
        {
          activePackage
        }
      );
    } else {
      return (
        <Price
          value={packagePrice}
          type={MENU}
          altText={intl.formatMessage({
            id: 'c8.services.page.packages.modal.button.add'
          })}
        />
      );
    }
  }
  getFooter() {
    const {
      packages,
      addPackage,
      selectedPackage,
      promotion,
      readOnly,
      canUpdate
    } = this.props;
    const showDiscountInFooter =
      promotion &&
      (promotion.promoType === 'percentApptDiscount' ||
        promotion.promoType === 'currencyApptDiscount');
    return (
      <tfoot className={`${ns}__footer`}>
        <tr className={`${ns}__service-row ${ns}__service-row--bottom`}>
          <td className={`${ns}__service-col ${ns}__service-col--big`} />
          {packages.map(pack => {
            const isSelected = pack.description === selectedPackage;
            const packagePrice = showDiscountInFooter
              ? calcPackageTotalPrice(pack.services, pack.totalPrice)
              : calcPackageTotalPrice(
                  pack.services,
                  pack.totalPrice,
                  promotion
                );
            const buttonClass = cn(`${ns}__button`, {
              [`${ns}__button--remove`]: isSelected
            });
            return (
              <td
                key={pack.name}
                className={`${ns}__service-col py-3 text-center`}
              >
                <Button
                  disabled={
                    pack.services.reduce((a, b) => a + b.isSelected, 0) < 1 ||
                    (readOnly && !canUpdate)
                  }
                  className={buttonClass}
                  onClick={() => addPackage(pack)}
                  tabIndex={0}
                  aria-label={this.getButtonContent(
                    isSelected,
                    packagePrice,
                    true
                  )}
                >
                  {this.getButtonContent(isSelected, packagePrice, false)}
                </Button>
              </td>
            );
          })}
        </tr>
      </tfoot>
    );
  }

  render() {
    const { packages, selectService, readOnly, quoteServices } = this.props;
    const allServices = [];
    packages.map(pack =>
      pack.services.map(service => allServices.push(service))
    );
    const allServicesIds = [];
    const joinedServices = allServices.filter(
      service =>
        allServicesIds.indexOf(service.id) === -1 &&
        allServicesIds.push(service.id)
    );
    joinedServices.sort(
      (a, b) => (a.order > b.order ? 1 : b.order > a.order ? -1 : 0)
    );
    const tableClass = cn(ns, {
      [`${ns}--short-columns`]: packages.length > 3
    });
    return (
      <table className={tableClass}>
        <thead className={`${ns}__header`}>
          <tr className={`${ns}__service-row ${ns}__service-row--top`}>
            <td className={`${ns}__service-col ${ns}__service-col--big`} />
            {packages.map(pack => (
              <td key={pack.name} className={`${ns}__service-col py-2`}>
                {pack.description}
              </td>
            ))}
          </tr>
        </thead>
        <tbody className={`${ns}__body`} tabIndex={0}>
          {joinedServices.map(service => {
            const {
              promotion,
              price,
              discountedPrice,
              showPrice,
              description,
              isExpanded
            } = service;
            const serviceLength =
              9 - (description ? 1 : 0) - (promotion ? 2 : 0);
            const serviceClass = cn(
              `col-sm-${serviceLength}`,
              `${ns}__service-name`,
              'd-inline-block',
              {
                [`${ns}__service-name--with-icon`]: description
              }
            );
            const rowClass = cn(`${ns}__service-row`, {
              [`${ns}__service-row--active`]: isExpanded
            });
            return (
              <tr key={service.id} className={rowClass}>
                <td
                  className={`${ns}__service-col ${ns}__service-col--big ${ns}__service`}
                >
                  <div>
                    {description && (
                      <div
                        className={`${ns}__icon-container col-sm-1 d-inline-block text-center`}
                      >
                        <Icon
                          type={`chevron__${
                            service.isExpanded ? 'up' : 'down'
                          }`}
                          className={`${ns}__icon`}
                          onClick={() => this.collapseService(service.id)}
                        />
                      </div>
                    )}
                    <div className={`${serviceClass} word-break`}>
                      {/* <Truncate className={`${ns}__title`} lines={2}> */}
                      {service.name}
                      {/* </Truncate> */}
                    </div>
                    {promotion && (
                      <div
                        className={'col-sm-2 d-inline-block p-0 text-center'}
                      >
                        <p className={`${ns}__price--promotion`}>
                          {showPrice && <Price value={price} type={MENU} />}
                        </p>
                      </div>
                    )}
                    <div className={`col-sm-3 d-inline-block p-0 text-center`}>
                      {showPrice && (
                        <Price value={promotion ? discountedPrice : price} />
                      )}
                    </div>
                  </div>
                  {service.isExpanded && (
                    <div
                      className={`${ns}__description col-offset-sm-1 word-break`}
                    >
                      {service.description}
                    </div>
                  )}
                </td>
                {packages.map((pack, index) => (
                  <td
                    key={pack.name}
                    className={`${ns}__service-col ${ns}__service-col--check ${ns}__service`}
                  >
                    {pack.services.map(packageService => {
                      const isQuoteService =
                        quoteServices &&
                        quoteServices.find(
                          quoteService => quoteService.id === packageService.id
                        );
                      return packageService.id === service.id ? (
                        packageService.selectable ? (
                          <Checkbox
                            disabled={
                              readOnly &&
                              isQuoteService &&
                              isQuoteService.selectByDefault
                            }
                            isSelected={packageService.isSelected}
                            onClick={() => {
                              selectService(packageService.id, index);
                            }}
                          />
                        ) : (
                          <Icon
                            className={`${ns}__check`}
                            key={packageService.id}
                            type={'check'}
                          />
                        )
                      ) : (
                        ''
                      );
                    })}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {this.getFooter()}
      </table>
    );
  }
}

PackagesTable.propTypes = {
  activePackage: PropTypes.string,
  addPackage: PropTypes.func.isRequired,
  canUpdate: PropTypes.bool,
  intl: PropTypes.object,
  packages: PropTypes.array.isRequired,
  promotion: PropTypes.object,
  selectedPackage: PropTypes.string,
  selectService: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  quoteServices: PropTypes.array
};

export default injectIntl(PackagesTable);



// WEBPACK FOOTER //
// ./src/components/molecules/PackagesTable/PackagesTable.js